import React, { useCallback, useEffect, useState } from 'react'

import { useAssessment } from '@/features/assessment/hooks'
import { useToast } from '@/hooks/useToast'
import type { Evaluation } from '@/types/assessment_process.types'
import { errorUtils } from '@/utils/query/common'

import { useAssessmentProcess } from '../hooks/useAssessmentProcess'
import { useCreateEvaluation, useUpdateEvaluation } from '../hooks/useEvaluationMutations'
import { usePlanItems } from '../hooks/usePlanningMutations'
import type {
  AssessmentProcessContextType,
  AssessmentProcessPhaseProgress,
  AssessmentProcessState,
  MadurezResumen
} from '../types/assessmentProcess.types'

import { AssessmentProcessContext } from './AssessmentProcessContext'

const initialState: AssessmentProcessState = {
  assessment: {
    id: '',
    version_id: '',
    nombre: '',
    codigo: '',
    organizacion_id: '',
    fecha_inicio: '',
    created_at: '',
    updated_at: ''
  },
  assessmentProgress: [],
  madurezResumen: { actual: null, objetivo: null, brecha: null },
  currentPhase: 'dashboard',
  currentPhaseProgress: null,
  // currentPhaseUnsavedChanges: false,
  loading: false,
  error: null,
  activeFunction: null,
  activeCategory: null,
  activeSubcategory: null,
  phaseNavigationKey: 0
}

interface AssessmentProcessProviderProps {
  children: React.ReactNode
  assessmentId: string
}

export const AssessmentProcessProvider: React.FC<AssessmentProcessProviderProps> = ({
  children,
  assessmentId
}) => {
  const [state, setState] = useState<AssessmentProcessState>(initialState)
  const { showToast } = useToast()

  // Use React Query hook for assessment data
  const {
    assessment,
    isLoading: isAssessmentLoading,
    error: assessmentError
  } = useAssessment({
    assessmentId,
    expandAll: true,
    enabled: !!assessmentId
  })

  // Use React Query hook for assessment process data
  const {
    assessmentProcess,
    isLoading: isProcessLoading,
    error: processError,
    refetch: refetchAssessmentProcess
  } = useAssessmentProcess({
    assessmentId,
    enabled: !!assessmentId
  })

  // Use React Query hook for plan items data (for Phase 4 progress calculation)
  const { data: planItems = [] } = usePlanItems(assessmentId)

  // Mutation hooks for evaluation management
  const createEvaluationMutation = useCreateEvaluation(assessmentId, {
    onSuccess: () => {
      // Refetch assessment process to update the navigation tree
      refetchAssessmentProcess()
      // Clear any previous errors
      setState((prev) => ({
        ...prev,
        error: null
      }))
      // Show success notification
      showToast({
        title: 'Evaluación creada',
        description: 'La evaluación se ha guardado exitosamente',
        type: 'success'
      })
    },
    onError: (error) => {
      console.error('Error al crear la evaluación:', error)
      // Update context error state to avoid navigation issues
      const errorMessage = errorUtils.transformAxiosError(error)
      setState((prev) => ({
        ...prev,
        error: errorMessage
      }))
      // Show the user notification via toast
      showToast({
        title: 'Error al crear evaluación',
        description: errorMessage,
        type: 'error'
      })
    }
  })

  const updateEvaluationMutation = useUpdateEvaluation(assessmentId, {
    onSuccess: () => {
      // Refetch assessment process to update the navigation tree
      refetchAssessmentProcess()
      // Clear any previous errors
      setState((prev) => ({
        ...prev,
        error: null
      }))
      // Show success notification
      showToast({
        title: 'Evaluación actualizada',
        description: 'Los cambios se han guardado exitosamente',
        type: 'success'
      })
    },
    onError: (error) => {
      console.error('Error al actualizar la evaluación:', error)
      // Update context error state to avoid navigation issues
      const errorMessage = errorUtils.transformAxiosError(error)
      setState((prev) => ({
        ...prev,
        error: errorMessage
      }))
      // Show the user notification via toast
      showToast({
        title: 'Error al actualizar evaluación',
        description: errorMessage,
        type: 'error'
      })
    }
  })

  // Sync assessment data from React Query with provider state
  useEffect(() => {
    if (assessment) {
      // Initialize progress for each phase
      const initialProgress: AssessmentProcessPhaseProgress[] = [
        { phase: 'maturity', completedItems: 0, totalItems: 100, percentage: 0, lastUpdated: null },
        {
          phase: 'gap-analysis',
          completedItems: 0,
          totalItems: 100,
          percentage: 0,
          lastUpdated: null
        },
        {
          phase: 'recommendations',
          completedItems: 0,
          totalItems: 50,
          percentage: 0,
          lastUpdated: null
        },
        { phase: 'planning', completedItems: 0, totalItems: 20, percentage: 0, lastUpdated: null }
      ]

      setState((prev) => ({
        ...prev,
        assessment,
        assessmentProgress: initialProgress,
        currentPhaseProgress: initialProgress[0],
        loading: false,
        error: null
      }))
    }
  }, [assessment])

  // Calculate progress based on assessment process data
  const calculateProgress = useCallback(() => {
    if (!assessmentProcess) return

    let totalSubcategories = 0
    let totalSubcategoriesWithGap = 0
    let totalSubcategoriesWithRecommendations = 0
    let maturityCompleted = 0
    let gapAnalysisCompleted = 0

    // Para madurezResumen (Madurez actual/objetivo/brecha, mismo criterio
    // que la fila "Madurez General" del reporte RPT-DET-01): promedios
    // sobre subcategorías con nivel válido (> -1), no sobre el total.
    let sumaNivelActual = 0
    let countNivelActual = 0
    let sumaNivelObjetivo = 0
    let countNivelObjetivo = 0
    let sumaBrecha = 0
    let countBrecha = 0

    for (const func of assessmentProcess.funciones) {
      for (const cat of func.categorias) {
        for (const sub of cat.subcategorias) {
          totalSubcategories++

          if (sub.evaluacion) {
            const tieneNivelActual =
              sub.evaluacion.nivel_actual !== undefined &&
              sub.evaluacion.nivel_actual !== null &&
              sub.evaluacion.nivel_actual > -1
            const tieneNivelObjetivo =
              sub.evaluacion.nivel_objetivo !== undefined &&
              sub.evaluacion.nivel_objetivo !== null &&
              sub.evaluacion.nivel_objetivo > -1

            // Count as maturity completed if has current level evaluation
            if (tieneNivelActual) {
              maturityCompleted++
              sumaNivelActual += sub.evaluacion.nivel_actual as number
              countNivelActual++
            }

            // Count as gap analysis completed if has target level evaluation
            if (tieneNivelObjetivo) {
              gapAnalysisCompleted++
              sumaNivelObjetivo += sub.evaluacion.nivel_objetivo as number
              countNivelObjetivo++
            }

            if (tieneNivelActual && tieneNivelObjetivo) {
              sumaBrecha += Math.max(
                0,
                (sub.evaluacion.nivel_objetivo as number) - (sub.evaluacion.nivel_actual as number)
              )
              countBrecha++
            }

            // Check if there is a gap between current and target levels
            if (
              tieneNivelActual &&
              tieneNivelObjetivo &&
              sub.evaluacion.nivel_objetivo! > sub.evaluacion.nivel_actual!
            ) {
              totalSubcategoriesWithGap++

              // If there is a gap, check if there are recommendations
              if (sub.recomendaciones && sub.recomendaciones.length > 0) {
                totalSubcategoriesWithRecommendations++
              }
            }
          }
        }
      }
    }

    const madurezResumen: MadurezResumen = {
      actual:
        countNivelActual > 0 ? Math.round((sumaNivelActual / countNivelActual) * 100) / 100 : null,
      objetivo:
        countNivelObjetivo > 0
          ? Math.round((sumaNivelObjetivo / countNivelObjetivo) * 100) / 100
          : null,
      brecha: countBrecha > 0 ? Math.round((sumaBrecha / countBrecha) * 100) / 100 : null
    }

    // Calcular porcentaje y redondear a 1 decimal
    const maturityPercentage =
      totalSubcategories > 0 ? Math.round((maturityCompleted / totalSubcategories) * 1000) / 10 : 0

    const gapAnalysisPercentage =
      totalSubcategories > 0
        ? Math.round((gapAnalysisCompleted / totalSubcategories) * 1000) / 10
        : 0

    const recommendationsPercentage =
      totalSubcategoriesWithGap > 0
        ? Math.round((totalSubcategoriesWithRecommendations / totalSubcategoriesWithGap) * 1000) /
          10
        : 0

    // Phase 4 - Planning progress
    const planTotalItems = planItems.length
    const planCompletedItems = planItems.filter(
      (i) => i.estado !== 'sin_planificar' && i.estado !== 'cancelado'
    ).length
    const planningPercentage =
      planTotalItems > 0 ? Math.round((planCompletedItems / planTotalItems) * 1000) / 10 : 0

    const updatedProgress: AssessmentProcessPhaseProgress[] = [
      {
        phase: 'maturity',
        completedItems: maturityCompleted,
        totalItems: totalSubcategories,
        percentage: maturityPercentage,
        lastUpdated: new Date()
      },
      {
        phase: 'gap-analysis',
        completedItems: gapAnalysisCompleted,
        totalItems: totalSubcategories,
        percentage: gapAnalysisPercentage,
        lastUpdated: new Date()
      },
      {
        phase: 'recommendations',
        completedItems: totalSubcategoriesWithRecommendations,
        totalItems: totalSubcategoriesWithGap,
        percentage: recommendationsPercentage,
        lastUpdated: null
      },
      {
        phase: 'planning',
        completedItems: planCompletedItems,
        totalItems: planTotalItems,
        percentage: planningPercentage,
        lastUpdated: new Date()
      }
    ]

    setState((prev) => ({
      ...prev,
      assessmentProgress: updatedProgress,
      madurezResumen,
      currentPhaseProgress:
        updatedProgress.find((p) => p.phase === prev.currentPhase) || updatedProgress[0]
    }))
  }, [assessmentProcess, planItems])

  // Sync assessment process data and calculate progress
  useEffect(() => {
    if (assessmentProcess) {
      setState((prev) => ({
        ...prev
        // Add assessmentProcess to state if needed
        // You may need to update the AssessmentProcessState type to include assessmentProcess
      }))

      // Calculate progress whenever assessment process data changes
      calculateProgress()
    }
  }, [assessmentProcess, calculateProgress])

  // Sync loading state from both React Query hooks
  useEffect(() => {
    const isLoading = isAssessmentLoading || isProcessLoading
    setState((prev) => ({
      ...prev,
      loading: isLoading
    }))
  }, [isAssessmentLoading, isProcessLoading])

  // Sync error state from both React Query hooks
  // Only sync critical loading errors, not mutation errors
  useEffect(() => {
    const error = assessmentError || processError
    if (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error
      }))
    }
  }, [assessmentError, processError])

  // Clear context error when assessment or assessmentProcess loads successfully
  useEffect(() => {
    if (assessment || assessmentProcess) {
      setState((prev) => ({
        ...prev,
        error: null
      }))
    }
  }, [assessment, assessmentProcess])

  // Remove the loadAssessment method since data is handled by React Query
  const loadAssessment = useCallback(async (_id: string) => {
    // This method is kept for interface compatibility but does nothing
    // since assessment loading is now handled by the useAssessment hook
    console.warn(
      'loadAssessment is deprecated. Assessment data is automatically loaded via React Query.'
    )
  }, [])

  // const saveUnsavedChanges = useCallback(async () => {
  //   if (!state.currentPhaseUnsavedChanges) return

  //   setState((prev) => ({ ...prev, loading: true }))
  //   try {
  //     // TODO: Implement save logic based on current phase
  //     // console.log('Saving unsaved changes for phase:', state.currentPhase)

  //     setState((prev) => ({
  //       ...prev,
  //       currentPhaseUnsavedChanges: false,
  //       loading: false
  //     }))
  //   } catch (error) {
  //     setState((prev) => ({
  //       ...prev,
  //       loading: false,
  //       error: error instanceof Error ? error.message : 'Error saving changes'
  //     }))
  //   }
  // }, [state.currentPhaseUnsavedChanges])

  const phase1UpdateCurrentMaturityLevel = useCallback(
    (subcategoryId: string, value: number, observations: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (!assessmentProcess || !assessment) {
          console.warn('Assessment process o assessment no disponible')
          reject(new Error('Assessment process o assessment no disponible'))
          return
        }

        // Find the evaluation for this subcategory
        let existingEvaluation: Evaluation | null = null
        for (const func of assessmentProcess.funciones) {
          for (const cat of func.categorias) {
            for (const sub of cat.subcategorias) {
              if (sub.codigo === subcategoryId && sub.evaluacion) {
                existingEvaluation = sub.evaluacion
                break
              }
            }
          }
        }

        if (existingEvaluation) {
          // Update existing evaluation
          updateEvaluationMutation.mutate(
            {
              evaluationId: existingEvaluation.id,
              data: {
                nivel_actual: value,
                observaciones_actuales: observations
              }
            },
            {
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            }
          )
        } else {
          // Create new evaluation
          createEvaluationMutation.mutate(
            {
              version_id: assessment.version_id,
              codigo_subcategoria: subcategoryId,
              punto_partida: -1, // Use -1 as starting point for new evaluations
              nivel_actual: value,
              observaciones_actuales: observations
            },
            {
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            }
          )
        }

        setState((prev) => ({
          ...prev
          // currentPhaseUnsavedChanges: true
        }))
      })
    },
    [assessmentProcess, assessment, createEvaluationMutation, updateEvaluationMutation]
  )

  const phase2UpdateTargetMaturityLevel = useCallback(
    (subcategoryId: string, value: number, observations: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (!assessmentProcess || !assessment) {
          console.warn('Assessment process o assessment no disponible')
          reject(new Error('Assessment process o assessment no disponible'))
          return
        }

        // Find the evaluation for this subcategory
        let existingEvaluation: Evaluation | null = null
        for (const func of assessmentProcess.funciones) {
          for (const cat of func.categorias) {
            for (const sub of cat.subcategorias) {
              if (sub.codigo === subcategoryId && sub.evaluacion) {
                existingEvaluation = sub.evaluacion
                break
              }
            }
          }
        }

        if (existingEvaluation) {
          // Update existing evaluation
          updateEvaluationMutation.mutate(
            {
              evaluationId: existingEvaluation.id,
              data: {
                nivel_objetivo: value,
                observaciones_objetivo: observations
              }
            },
            {
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            }
          )
        } else {
          // Cannot set target level if no existing evaluation with current level
          console.warn(
            'No se puede establecer el nivel objetivo de una evaluación que no existe y por ello no hay nivel actual'
          )
          reject(
            new Error(
              'No se puede establecer el nivel objetivo de una evaluación que no existe y por ello no hay nivel actual'
            )
          )
        }

        setState((prev) => ({
          ...prev
          // currentPhaseUnsavedChanges: true
        }))
      })
    },
    [assessmentProcess, assessment, updateEvaluationMutation]
  )

  const setActiveFunction = useCallback((functionId: string | null) => {
    setState((prev) => ({ ...prev, activeFunction: functionId }))
  }, [])

  const setActiveCategory = useCallback((categoryId: string | null) => {
    setState((prev) => ({ ...prev, activeCategory: categoryId }))
  }, [])

  const setActiveSubcategory = useCallback((subcategoryId: string | null) => {
    setState((prev) => ({ ...prev, activeSubcategory: subcategoryId }))
  }, [])

  const incrementPhaseNavigationKey = useCallback(() => {
    setState((prev) => ({ ...prev, phaseNavigationKey: prev.phaseNavigationKey + 1 }))
  }, [])

  const contextValue: AssessmentProcessContextType = {
    ...state,
    loadAssessment,
    // saveUnsavedChanges,
    phase1UpdateCurrentMaturityLevel,
    phase2UpdateTargetMaturityLevel,
    setActiveFunction,
    setActiveCategory,
    setActiveSubcategory,
    calculateProgress,
    incrementPhaseNavigationKey
  }

  return (
    <AssessmentProcessContext.Provider value={contextValue}>
      {children}
    </AssessmentProcessContext.Provider>
  )
}
